import { useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Button,
  Dropdown,
  InlineLoading,
  NumberInput,
  Tag,
  TextInput,
} from '@carbon/react';
import { Add, ChevronDown, ChevronUp, TrashCan } from '@carbon/icons-react';
import { NumberField } from 'components/form-fields-2';
import type {
  ExpenseEnumsResponse,
  ExpenseLineItem,
  InterestContext,
  PercentBasis,
  TimingAnchor,
} from '../types';
import {
  STANDARD_OPEX_PRESET,
  type DefaultTiming,
  defaultTiming,
  filterPresetToAdd,
  formatItemCadence,
  formatItemReference,
  formatItemTiming,
  getLoeYear,
  getSubtypes,
  isBuyerCommission,
  isLoeItem,
  isTerminalAnchor,
  LOE_TIMING_LABEL,
  needsPercentBasis,
  resolveTimingAnchor,
  subtypeTimingPatch,
  subtypeUnit,
  timingAnchorPatch,
} from './line-item-data';
import { enumsLoadableAtom } from './atoms';
import { BuyerCommissionEditor } from './buyer-commission-editor';
import styles from './expense-line-items-editor.module.scss';

type AmountType = ExpenseLineItem['amountType'];

interface ExpenseLineItemsEditorProps {
  items: ExpenseLineItem[];
  onChange: (items: ExpenseLineItem[]) => void;
}

// Next available numeric id for a new line item.
const nextId = (items: ExpenseLineItem[]): number =>
  items.reduce((max, item) => Math.max(max, item.id), 0) + 1;

// A blank line item with the given id, carrying the cadence and reference date
// its class and subtype imply. Both are real values the calculator acts on,
// never placeholders -- see defaultTiming.
const blankItem = (
  id: number,
  subtype: string,
  expenseClass: ExpenseLineItem['expenseClass'],
  timing: DefaultTiming
): ExpenseLineItem => ({
  id,
  expenseClass,
  subtype,
  context: undefined,
  description: '',
  amountType: 'fixed',
  amount: null,
  ...timing,
});

/**
 * Editable Expense Line Items section: per-row Expense Type, Subtype, Interest
 * Context, Description, Unit (amount type) and Value, plus an inline Timing
 * editor. Footer adds blank rows, the Standard OpEx preset, or LOE years.
 * @param {ExpenseLineItemsEditorProps} props - The items and change handler
 * @return {JSX.Element} The line items editor
 */
export function ExpenseLineItemsEditor({
  items,
  onChange,
}: ExpenseLineItemsEditorProps): JSX.Element {
  const [openTimingId, setOpenTimingId] = useState<number | null>(null);
  const enumsState = useAtomValue(enumsLoadableAtom);
  const enums = enumsState.state === 'hasData' ? enumsState.data : null;

  const update = (id: number, patch: Partial<ExpenseLineItem>) =>
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const remove = (id: number) => onChange(items.filter((it) => it.id !== id));

  // The three add paths all need the enums to resolve a reference date, and
  // none of their buttons render before the enums load, so the guard is a
  // type narrowing rather than a reachable branch.
  const add = () => {
    if (!enums) return;
    const subtype = getSubtypes(enums, 'opex')[0] ?? '';
    onChange([
      ...items,
      blankItem(
        nextId(items),
        subtype,
        'opex',
        defaultTiming(enums, subtype, 'opex')
      ),
    ]);
    setOpenTimingId(null);
  };

  const addStandardOpex = () => {
    if (!enums) return;
    const toAdd = filterPresetToAdd(STANDARD_OPEX_PRESET, items);
    if (!toAdd.length) return;
    let id = nextId(items);
    onChange([
      ...items,
      // The preset is a module constant and so cannot read the enums; its
      // reference date is stamped here, where they are in scope.
      ...toAdd.map((item) => ({
        ...item,
        id: id++,
        ...defaultTiming(enums, item.subtype, item.expenseClass),
      })),
    ]);
  };

  const addLoeYear = () => {
    if (!enums) return;
    const subtype = `LOE — Year ${items.filter(isLoeItem).length + 1}`;
    onChange([
      ...items,
      {
        ...blankItem(
          nextId(items),
          subtype,
          'opex',
          defaultTiming(enums, subtype, 'opex')
        ),
        context: 'wi_owner',
      },
    ]);
  };

  // LOE items float to the top, ordered by year; everything else follows.
  const loeItems = [...items.filter(isLoeItem)].sort(
    (a, b) => getLoeYear(a.subtype) - getLoeYear(b.subtype)
  );
  const otherItems = items.filter((i) => !isLoeItem(i));
  const sortedItems = [...loeItems, ...otherItems];

  // The highest LOE year carries forward indefinitely — flag it for the badge.
  const lastLoeId =
    loeItems.length > 0 ? loeItems[loeItems.length - 1].id : null;

  // Standard OpEx preset is "loaded" once nothing in it remains to add.
  const presetExhausted =
    filterPresetToAdd(STANDARD_OPEX_PRESET, items).length === 0;

  // Standard rows have Unit + Value columns that need headers. Buyer Commission
  // rows replace those columns with the inline At Target / At Max editor and
  // need that header space blank, so only show the headers when at least one
  // non–Buyer-Commission row is present.
  const showAmountHeaders = sortedItems.some((it) => !isBuyerCommission(it));

  // Removing an LOE year renumbers the remaining years so they stay contiguous.
  const removeLoe = (id: number) => {
    const remaining = loeItems.filter((i) => i.id !== id);
    onChange(
      items
        .filter((it) => it.id !== id)
        .map((it) => {
          const idx = remaining.findIndex((r) => r.id === it.id);
          return idx === -1 ? it : { ...it, subtype: `LOE — Year ${idx + 1}` };
        })
    );
  };

  // Line-item dropdowns are driven by the BE enums; hold rendering until loaded.
  if (!enums) {
    return (
      <div className={styles.lineItems}>
        <InlineLoading description="Loading expense options…" />
      </div>
    );
  }

  const classItems = enums.expenseClasses.map((o) => o.value);
  const contextItems = ['', ...enums.contexts.map((o) => o.value)];
  const amountTypeItems = enums.amountTypes.map((o) => o.value);
  const percentBasisItems = enums.percentBases.map((o) => o.value);
  const labelFor = (opts: { value: string; label: string }[], v: string) =>
    opts.find((o) => o.value === v)?.label ?? v;

  const timingPatchForSubtype = (item: ExpenseLineItem, subtype: string) =>
    subtypeTimingPatch(enums, item, subtype);

  return (
    <div className={styles.lineItems}>
      {/* Every expense is applied per slot (per well) — a fixed dollar amount is
          charged to each matched slot individually, not once for the package. */}
      <p className={styles.perSlotNote}>
        Values are applied <strong>per slot</strong> (per well). A fixed dollar
        amount is charged to each matched slot individually — not once per
        package.
      </p>
      {sortedItems.length > 0 ? (
        <div className={styles.table}>
          <div className={styles.header}>
            <span>Expense Type</span>
            <span>Subtype</span>
            <span>Interest Context</span>
            <span>Description</span>
            {/* Unit + Value headers apply to standard rows only; Buyer
                Commission rows use inline At Target / At Max labels instead. */}
            <span>{showAmountHeaders ? 'Unit' : ''}</span>
            <span>{showAmountHeaders ? 'Value' : ''}</span>
            <span>Timing</span>
            <span />
          </div>

          {sortedItems.map((item) => {
            const isLoe = isLoeItem(item);
            const isBc = isBuyerCommission(item);
            const isLastLoe = item.id === lastLoeId;
            const subtypes = getSubtypes(
              enums,
              item.expenseClass,
              item.context
            );
            const subtypeItems = subtypes.includes(item.subtype)
              ? subtypes
              : [item.subtype, ...subtypes];
            const unit = subtypeUnit(item.subtype);
            const timingLabel = formatItemTiming(item);
            const timingCadence = formatItemCadence(item);
            const timingReference = formatItemReference(item);
            const timingOpen = openTimingId === item.id;

            return (
              <div key={item.id} className={styles.rowWrap}>
                <div className={styles.row}>
                  {/* Expense Type */}
                  <div className={styles.cell}>
                    <Dropdown
                      autoAlign
                      id={`li-class-${item.id}`}
                      titleText=""
                      label="Select..."
                      hideLabel
                      size="sm"
                      disabled={isLoe}
                      items={classItems}
                      selectedItem={item.expenseClass}
                      itemToString={(v: string) =>
                        labelFor(enums.expenseClasses, v)
                      }
                      onChange={({
                        selectedItem,
                      }: {
                        selectedItem: string;
                      }) => {
                        const cls =
                          (selectedItem as ExpenseLineItem['expenseClass']) ??
                          'opex';
                        const subtype = getSubtypes(
                          enums,
                          cls,
                          item.context
                        )[0];
                        // Re-apply the whole default timing alongside the
                        // subtype reset this already did: switching class
                        // re-characterises the item, and leaving monthly
                        // recurrence -- or first production -- on a one-off
                        // CapEx is the worse error.
                        update(item.id, {
                          expenseClass: cls,
                          subtype,
                          ...defaultTiming(enums, subtype, cls),
                        });
                      }}
                    />
                  </div>

                  {/* Subtype */}
                  <div className={styles.cell}>
                    {isLoe ? (
                      <>
                        <TextInput
                          id={`li-subtype-${item.id}`}
                          labelText=""
                          hideLabel
                          size="sm"
                          value={item.subtype}
                          readOnly
                        />
                        {isLastLoe && (
                          <Tag
                            type="teal"
                            size="sm"
                            className={styles.carryFwdTag}
                          >
                            carry-fwd
                          </Tag>
                        )}
                      </>
                    ) : (
                      <Dropdown
                        autoAlign
                        id={`li-subtype-${item.id}`}
                        titleText=""
                        label="Select..."
                        hideLabel
                        size="sm"
                        items={subtypeItems}
                        selectedItem={item.subtype}
                        itemToString={(i: string) => {
                          const u = subtypeUnit(i);
                          return u ? `${i} (${u})` : (i ?? '');
                        }}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string;
                        }) => {
                          const subtype = selectedItem ?? '';
                          update(item.id, {
                            subtype,
                            ...timingPatchForSubtype(item, subtype),
                          });
                        }}
                      />
                    )}
                  </div>

                  {/* Interest Context */}
                  <div className={styles.cell}>
                    <Dropdown
                      autoAlign
                      id={`li-context-${item.id}`}
                      titleText=""
                      label="Any"
                      hideLabel
                      size="sm"
                      items={contextItems}
                      selectedItem={item.context ?? ''}
                      itemToString={(v: string) => {
                        return v ? labelFor(enums.contexts, v) : 'Any';
                      }}
                      onChange={({
                        selectedItem,
                      }: {
                        selectedItem: string;
                      }) => {
                        const newCtx =
                          (selectedItem as InterestContext) || undefined;
                        const newSubtypes = getSubtypes(
                          enums,
                          item.expenseClass,
                          newCtx
                        );
                        const patch: Partial<ExpenseLineItem> = {
                          context: newCtx,
                        };
                        if (!newSubtypes.includes(item.subtype)) {
                          patch.subtype = newSubtypes[0];
                          Object.assign(
                            patch,
                            timingPatchForSubtype(item, newSubtypes[0])
                          );
                        }
                        update(item.id, patch);
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div className={styles.cell}>
                    <TextInput
                      id={`li-desc-${item.id}`}
                      labelText=""
                      hideLabel
                      size="sm"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        update(item.id, { description: e.target.value })
                      }
                    />
                  </div>

                  {/* Amount — Buyer Commission replaces the standard Unit +
                      Value cells with the At Target / At Max editor. */}
                  {isBc ? (
                    <div className={`${styles.cell} ${styles.bcCell}`}>
                      <BuyerCommissionEditor
                        item={item}
                        onUpdate={(patch) => update(item.id, patch)}
                      />
                    </div>
                  ) : (
                    <>
                      {/* Unit (amount type) */}
                      <div className={styles.cell}>
                        <Dropdown
                          autoAlign
                          id={`li-unit-${item.id}`}
                          titleText=""
                          label="Select..."
                          hideLabel
                          size="sm"
                          items={amountTypeItems}
                          selectedItem={item.amountType}
                          itemToString={(v: AmountType) => {
                            return v ? labelFor(enums.amountTypes, v) : '';
                          }}
                          onChange={({
                            selectedItem,
                          }: {
                            selectedItem: AmountType;
                          }) =>
                            update(item.id, {
                              amountType: selectedItem ?? 'fixed',
                            })
                          }
                        />
                        {unit && (
                          <span className={styles.unitHint}>{unit}</span>
                        )}
                      </div>

                      {/* Value — NumberField keeps a local display buffer so
                          in-progress fragments (a lone ".", "0.", "1.50") are
                          preserved instead of being erased mid-entry. */}
                      <div className={styles.cell}>
                        <NumberField
                          id={`li-value-${item.id}`}
                          hideLabel
                          light
                          size="sm"
                          placeholder="—"
                          value={item.amount}
                          onChange={(v) => update(item.id, { amount: v })}
                        />
                      </div>
                    </>
                  )}

                  {/* Timing — an LOE year row has a fixed monthly cadence, so
                      the timing editor is disabled for it.

                      MONTHLY, not annual. "Year N" selects WHICH monthly rate
                      applies during year N of production; it does not make the
                      amount annual. eval_calculator charges the year's schedule
                      entry on every monthly row with no /12
                      (append_costs.py#apply_loe_fixed_well_costs), and the
                      eval-2 Scenario form labels the same field "$/month".
                      This cell used to read "Annual", which invited amounts 12x
                      too small. */}
                  <div className={styles.cell}>
                    {isLoe ? (
                      <span className={styles.timingNa}>
                        {LOE_TIMING_LABEL}
                      </span>
                    ) : (
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={timingOpen ? ChevronUp : ChevronDown}
                        className={styles.timingButton}
                        title={timingLabel}
                        onClick={() =>
                          setOpenTimingId(timingOpen ? null : item.id)
                        }
                      >
                        {/* Stacked, not one line. Cadence and reference date
                            together run to 18rem at the longest, which is more
                            width than the row can give up; stacked they fit in
                            14rem. The full string stays on the button title. */}
                        <span className={styles.timingButtonLabel}>
                          <span className={styles.timingCadence}>
                            {timingCadence}
                          </span>
                          <span className={styles.timingReference}>
                            {timingReference}
                          </span>
                        </span>
                      </Button>
                    )}
                  </div>

                  {/* Delete */}
                  <div className={`${styles.cell} ${styles.cellAction}`}>
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={TrashCan}
                      iconDescription="Remove line item"
                      tooltipPosition="left"
                      onClick={() => {
                        if (isLoe) removeLoe(item.id);
                        else remove(item.id);
                      }}
                    />
                  </div>
                </div>

                {needsPercentBasis(item) && (
                  <div className={styles.percentBasis}>
                    <span className={styles.percentBasisLabel}>
                      Percentage of
                    </span>
                    <div className={styles.percentBasisField}>
                      <Dropdown
                        autoAlign
                        id={`percent-basis-${item.id}`}
                        titleText="Percentage of"
                        hideLabel
                        label="Select basis…"
                        size="sm"
                        items={percentBasisItems}
                        selectedItem={item.percentBasis ?? null}
                        itemToString={(v: PercentBasis | null) => {
                          return v ? labelFor(enums.percentBases, v) : '';
                        }}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: PercentBasis | null;
                        }) =>
                          update(item.id, {
                            percentBasis: selectedItem ?? undefined,
                          })
                        }
                      />
                    </div>
                    {!item.percentBasis && (
                      <span className={styles.percentBasisHint}>
                        Required for percentage-based expenses
                      </span>
                    )}
                  </div>
                )}

                {timingOpen && !isLoe && (
                  <TimingEditor
                    item={item}
                    enums={enums}
                    onUpdate={(patch) => update(item.id, patch)}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className={styles.empty}>
          No expense line items yet. Add one below or load Standard OpEx.
        </p>
      )}

      <div className={styles.footer}>
        <Button kind="tertiary" size="sm" renderIcon={Add} onClick={add}>
          Add Line Item
        </Button>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Add}
          onClick={addStandardOpex}
          disabled={presetExhausted}
        >
          Standard OpEx
        </Button>
        <Button kind="ghost" size="sm" renderIcon={Add} onClick={addLoeYear}>
          LOE Year
        </Button>
      </div>
    </div>
  );
}

interface TimingEditorProps {
  item: ExpenseLineItem;
  enums: ExpenseEnumsResponse;
  onUpdate: (patch: Partial<ExpenseLineItem>) => void;
}

/**
 * Inline timing editor for a single line item: the reference date the item
 * counts from, a start offset, a one-time vs recurring toggle, and (when
 * recurring) interval and duration controls.
 * @param {TimingEditorProps} props - The item, enums and update handler
 * @return {JSX.Element} The timing editor
 */
function TimingEditor({
  item,
  enums,
  onUpdate,
}: TimingEditorProps): JSX.Element {
  const offset = item.offsetMonths ?? 0;
  const recurring = item.isRecurring ?? false;
  const interval = item.recurringIntervalMonths ?? 1;
  const occurrences = item.recurringOccurrences ?? 'continuous';
  const anchor = resolveTimingAnchor(item);
  const terminal = isTerminalAnchor(anchor);

  const anchorLabel = (v: string): string =>
    enums.timingAnchors.find((o) => o.value === v)?.label ?? v;

  // The BE labels are written to finish the sentence "months after ...", so
  // only the first letter changes: "Property first production" becomes
  // "months after property first production".
  const anchorPhrase = (v: string): string => {
    const label = anchorLabel(v);
    return label.charAt(0).toLowerCase() + label.slice(1);
  };

  return (
    <div className={styles.timing}>
      <div className={styles.timingTitle}>Timing</div>

      <div className={styles.timingRow}>
        <span className={styles.timingLabel}>Reference date</span>
        <div className={styles.timingField}>
          <Dropdown
            autoAlign
            id={`li-timing-anchor-${item.id}`}
            titleText=""
            hideLabel
            label="Select…"
            size="sm"
            className={styles.timingAnchorSelect}
            items={enums.timingAnchors.map((o) => o.value)}
            selectedItem={anchor}
            itemToString={anchorLabel}
            onChange={({
              selectedItem,
            }: {
              selectedItem: TimingAnchor | null;
            }) => onUpdate(timingAnchorPatch(selectedItem ?? undefined))}
          />
          <span className={styles.timingHint}>
            what the start offset counts from
          </span>
        </div>
      </div>

      <div className={styles.timingRow}>
        <span className={styles.timingLabel}>Start offset</span>
        <div className={styles.timingField}>
          <NumberInput
            id={`li-timing-offset-${item.id}`}
            label=""
            hideLabel
            size="sm"
            min={0}
            step={1}
            value={offset}
            onChange={(_e: unknown, { value }: { value: number | string }) =>
              onUpdate({
                offsetMonths: Math.max(0, Number(value) || 0),
              })
            }
          />
          <span className={styles.timingHint}>
            months after {anchorPhrase(anchor)}
          </span>
        </div>
      </div>

      <div className={styles.timingRow}>
        <span className={styles.timingLabel}>Recurrence</span>
        <div className={styles.seg} role="group" aria-label="Recurrence">
          <button
            type="button"
            className={`${styles.segBtn} ${
              !recurring ? styles.segBtnActive : ''
            }`}
            aria-pressed={!recurring}
            onClick={() => onUpdate({ isRecurring: false })}
          >
            One-time
          </button>
          {/* Selecting the end of economic life already dropped any recurrence
              (timingAnchorPatch). Disabling the toggle is what stops the user
              putting it straight back and rebuilding the plugging-cost-forever
              combination -- the model rejects that write, but being told no
              after the fact is worse than not being offered it. */}
          <button
            type="button"
            className={`${styles.segBtn} ${
              recurring ? styles.segBtnActive : ''
            }`}
            aria-pressed={recurring}
            disabled={terminal}
            onClick={() =>
              onUpdate({
                isRecurring: true,
                recurringIntervalMonths: interval || 1,
                recurringOccurrences: occurrences || 'continuous',
              })
            }
          >
            Recurring
          </button>
        </div>
        {terminal && (
          <span className={styles.timingHint}>
            an expense at the end of economic life happens once
          </span>
        )}
      </div>

      {recurring && (
        <>
          <div className={styles.timingRow}>
            <span className={styles.timingLabel}>Interval</span>
            <div className={styles.timingField}>
              <span className={styles.timingHint}>Every</span>
              <NumberInput
                id={`li-timing-interval-${item.id}`}
                label=""
                hideLabel
                size="sm"
                min={1}
                step={1}
                value={interval}
                onChange={(
                  _e: unknown,
                  { value }: { value: number | string }
                ) =>
                  onUpdate({
                    recurringIntervalMonths: Math.max(1, Number(value) || 1),
                  })
                }
              />
              <span className={styles.timingHint}>
                months (1 = monthly · 3 = quarterly · 12 = annually)
              </span>
            </div>
          </div>

          <div className={styles.timingRow}>
            <span className={styles.timingLabel}>Duration</span>
            <div className={styles.seg} role="group" aria-label="Duration">
              <button
                type="button"
                className={`${styles.segBtn} ${
                  occurrences === 'continuous' ? styles.segBtnActive : ''
                }`}
                aria-pressed={occurrences === 'continuous'}
                onClick={() => onUpdate({ recurringOccurrences: 'continuous' })}
              >
                Continuous
              </button>
              <button
                type="button"
                className={`${styles.segBtn} ${
                  occurrences !== 'continuous' ? styles.segBtnActive : ''
                }`}
                aria-pressed={occurrences !== 'continuous'}
                onClick={() =>
                  onUpdate({
                    recurringOccurrences:
                      occurrences === 'continuous' ? 12 : occurrences,
                  })
                }
              >
                # Occurrences
              </button>
            </div>
            {occurrences !== 'continuous' && (
              <div className={styles.timingField}>
                <NumberInput
                  id={`li-timing-occ-${item.id}`}
                  label=""
                  hideLabel
                  size="sm"
                  min={1}
                  step={1}
                  value={occurrences}
                  onChange={(
                    _e: unknown,
                    { value }: { value: number | string }
                  ) =>
                    onUpdate({
                      recurringOccurrences: Math.max(1, Number(value) || 1),
                    })
                  }
                />
                <span className={styles.timingHint}>occurrences</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
